import OnspacePlayerControls from '@onpace/onspace-media/elements/player/controls'
import OnspaceVideoPlayerDebug from '@onpace/onspace-media/elements/player/video/debug'

const isTouchScreen = window.matchMedia('(pointer: coarse)').matches

/// The Onspace Player Controls is an element to control a Video Player.
export default class OnspaceVideoPlayerControls extends OnspacePlayerControls {
  /// Runs when the controls element is first connected to the DOM.
  runFirstConnected(options = {}) {
    super.runFirstConnected(options)

    this.classList.add('onspace-player-controls--video')

    if (!isTouchScreen) {
      this.addEventListener('mousemove', this.mouseMoved.bind(this))
      this.addEventListener('mouseleave', this.mouseLeft.bind(this))
    }

    this.becomeVisible()
  }

  ////////// Interaction

  ///// Pointer

  /// Responds to presses on the element.
  ///
  /// If the player wasn't initially able to resume playback with audio, this will do that. It otherwise behaves
  /// differently depending on the pointer type:
  /// - For mice, this will toggle playback.
  /// - For touch, this will toggle the visibility of the element.
  pressed(event) {
    super.pressed(event)

    if (this.player.audioPlaybackPrevented) {
      this.player.continueAudioPlayback()
    } else if (!this.player.playbackStarted) {
      this.player.startPlayer()
    } else if (this.tabsElement.expanded) {
      this.tabsElement.selectedTabIndex = null
    } else if (isTouchScreen) {
      this.toggleVisibility(true)
    } else {
      this.player.togglePlayback()
    }
  }

  /// Responds to mouse movement over the element.
  ///
  /// This will make the element visible.
  mouseMoved(_event) {
    this.becomeVisible()

    this.cancelUpNextAutomatic()
  }

  /// Responds to the mouse leaving the element.
  ///
  /// This will make the element invisible.
  mouseLeft(_event) {
    this.becomeInvisible()
  }

  ///// Keyboard

  /// Responds to keyboard presses beginning on the document.
  ///
  /// This overrides OnspaceMediaPlayer.keyPressBegan to respond to additional key commands specific to video content.
  keyPressBegan(event) {
    switch (event.key) {
    case 'f':
      this.player.toggleFullscreen()
      break
    case 'p':
      this.player.togglePictureInPicture()
      break
    default:
      return super.keyPressBegan(event)
    }

    event.preventDefault()
    event.stopPropagation()
    return false
  }

  /// Responds to keyboard presses on the escape key.
  ///
  /// This overrides OnspaceMediaPlayer.handleEscapeKeyPress to hide the controls when nothing else has been handled.
  handleEscapeKeyPress() {
    if (super.handleEscapeKeyPress()) {
      return true
    } else if (this.isVisible) {
      this.becomeInvisible(true)
      return true
    }

    return false
  }

  ////////// Events

  /// Attaches the event listeners for the given player.
  attachPlayerEvents(player) {
    super.attachPlayerEvents(player)

    this.addPlayerEventListener(player, 'onspace:media:player:video-presentation-changed', this.playerPresentationChanged)
  }

  /// Removes the event listeners from the given player.
  detachPlayerEvents(player) {
    this.removePlayerEventListener(player, 'onspace:media:player:video-presentation-changed')
  }

  ////////// Player

  /// Responds to the control instance's player changing.
  playerDidChange() {
    super.playerDidChange()

    this.updatePresentationControls()
  }

  /// Responds to the player changing its metadata.
  playerMetadataChanged(event) {
    super.playerMetadataChanged(event)

    this.updatePlaceholder()
  }

  /// Responds to the player changin its loading state.
  playerLoadingChanged(event) {
    super.playerLoadingChanged(event)

    this.becomeVisible()
  }

  /// Responds to the playback state of the player changing.
  playerPlaybackChanged(event) {
    super.playerPlaybackChanged(event)

    this.becomeVisible()
  }

  /// Responds to the presentation state of the player changing.
  playerPresentationChanged(_event) {
    this.updatePresentationControls()
  }

  ////////// Visibility

  /// Determines if the element can be hidden.
  get canBeInvisible() {
    return this.player.isPlaying && !this.player.isLoading && !this.player.pictureInPictureActive && !this.player.remotePlaybackActive && !this.menuVisible && !this.positionScrubberActive && !this.tabsElement.expanded
  }

  /// Indicates if the element is currently visible.
  get isVisible() {
    return this.classList.contains('onspace-player-controls--visible')
  }

  /// Makes the element visible or invisible, depending on the current state.
  toggleVisibility(force = false) {
    if (this.isVisible) {
      this.becomeInvisible(force)
    } else {
      this.becomeVisible()
    }
  }

  /// Makes the element visible.
  ///
  /// Unless the element can't be hidden, this will automatically hide the element again after a short period.
  becomeVisible() {
    if (this.visibilityTimeout) {
      window.clearTimeout(this.visibilityTimeout)
      this.visibilityTimeout = null
    }

    this.classList.add('onspace-player-controls--visible')
    this.updatePlayerExclusion()
    this.detectPositionScrubberAnimation()

    if (this.canBeInvisible) {
      this.visibilityTimeout = window.setTimeout(() => this.becomeInvisible(), 4000)
    }
  }

  /// Makes the element invisible.
  becomeInvisible(force = false) {
    if (!this.canBeInvisible && !force) { return }

    if (this.visibilityTimeout) {
      window.clearTimeout(this.visibilityTimeout)
      this.visibilityTimeout = null
    }

    this.classList.remove('onspace-player-controls--visible')
    this.updatePlayerExclusion()
    this.detectPositionScrubberAnimation()
  }

  ////////// Structure

  /// Sets up the content which should be displayed at all times.
  setupInitialContent() {
    super.setupInitialContent()

    if (!this.player.autoplay) {
      this.setupPlaceholder()
    }
  }

  /// Sets up the content which should only be displayed after playback has begun.
  ///
  /// This overrides OnspaceMediaControls.setupPlaybackContent to additionally initialise the presentation controls.
  setupPlaybackContent() {
    super.setupPlaybackContent()

    this.destroyPlaceholder()
    this.setupPresentationControls()
  }

  /// Updates the player's exclusion area.
  updatePlayerExclusion() {
    if (this.isVisible) {
      const footerRect = this.footerElement.getBoundingClientRect()
      this.player.setExclusionBottom(footerRect.height)
    } else {
      this.player.setExclusionBottom(0)
    }
  }

  ////////// Placeholder

  /// Initialises and configures the placeholder element.
  setupPlaceholder() {
    if (this.placeholderElement) { return }

    this.placeholderElement = document.createElement('div')
    this.placeholderElement.classList.add('onspace-player-controls__placeholder')
    if (typeof this.player.metadata === 'object' && this.player.metadata.artwork) { this.placeholderElement.style.backgroundImage = `url(${this.player.metadata.artwork})` }

    const iconElement = SVGElement.createOnspaceSpritemapSvg('onspace/player_play')
    this.placeholderElement.appendChild(iconElement)

    this.prepend(this.placeholderElement)
  }

  /// Updates the placeholder element, if it exists.
  updatePlaceholder() {
    if (!this.placeholderElement) { return }

    if (this.player.metadata.artwork) {
      this.placeholderElement.style.backgroundImage = `url(${this.player.metadata.artwork})`
    } else {
      this.placeholderElement.style.backgroundImage = ''
    }
  }

  /// Removes the placeholder from the DOM and clears the reference.
  destroyPlaceholder() {
    if (this.placeholderElement) {
      this.placeholderElement.remove()
      this.placeholderElement = null
    }
  }

  ////////// Menu

  /// Displays a menu overlay with the given items.
  ///
  /// This overrides OnspaceMediaControls.showMenu to update the visibility state.
  showMenu() {
    super.showMenu(...arguments)

    this.becomeVisible()
  }

  /// Hides and removes the menu overlay.
  ///
  /// This overrides OnspaceMediaControls.hideMenu to update the visibility state.
  hideMenu() {
    super.hideMenu(...arguments)

    this.becomeVisible()
  }

  ////////// Scrubbers

  /// Determines if the position scrubber should be animating.
  get canAnimatePositionScrubber() {
    return super.canAnimatePositionScrubber && (this.isVisible || this.player.presentationMode === 'pictureinpicture' || this.player.debugMode)
  }

  /// Responds to presses on the position scrubber.
  positionScrubberPressBegan(event) {
    super.positionScrubberPressBegan(event)

    this.positionScrubberActive = true
    this.becomeVisible()
  }

  /// Responds to a press ending from scrubbing the position.
  positionScrubberPressEnded(event) {
    super.positionScrubberPressEnded(event)

    this.positionScrubberActive = false
    this.becomeVisible()
  }

  ////////// Presentation

  /// Initialises the elements which control presentation.
  setupPresentationControls() {
    if (this.pictureInPictureButton) { return }

    this.pictureInPictureButton = this.barRightElement.addButton('pip', { icon: 'onspace/player_pip_enter', pressed: this.pictureInPicturePressed.bind(this) })
    this.fullscreenButton = this.barRightElement.addButton('fullscreen', { icon: 'onspace/player_fullscreen_enter', pressed: this.fullscreenPressed.bind(this) })

    this.updatePresentationControls()
  }

  /// Updates the state of presentation controls.
  updatePresentationControls() {
    if (!this.fullscreenButton) { return }

    this.detectPositionScrubberAnimation()

    if (this.player.pictureInPictureActive) {
      this.pictureInPictureButton.style.display = ''
      this.pictureInPictureButton.setIcon('onspace/player_pip_exit')
    } else if (this.player.canEnterPictureInPicture) {
      this.pictureInPictureButton.style.display = ''
      this.pictureInPictureButton.setIcon('onspace/player_pip_enter')
    } else {
      this.pictureInPictureButton.style.display = 'none'
    }

    if (this.player.canEnterFullscreen) {
      this.fullscreenButton.style.display = ''
      this.fullscreenButton.setIcon('onspace/player_fullscreen_enter')
    } else if (this.player.fullscreenActive && !isTouchScreen) {
      this.fullscreenButton.style.display = ''
      this.fullscreenButton.setIcon('onspace/player_fullscreen_exit')
    } else {
      this.fullscreenButton.style.display = 'none'
    }
  }

  ///// Events

  /// Responds to presses on the Picture in Picture button.
  pictureInPicturePressed(_event) {
    this.player.togglePictureInPicture()
  }

  /// Responds to presses on the Full Screen button.
  fullscreenPressed(_event) {
    this.player.toggleFullscreen()
  }

  ////////// Tabs

  ///// Events

  /// Responds to the tabs element showing its content.
  tabsExpanded(event) {
    super.tabsExpanded(event)

    this.becomeVisible()
  }

  /// Responds to the tabs element hiding its content.
  tabsCollapsed(event) {
    super.tabsCollapsed(event)

    this.becomeVisible()
  }

  ////////// Debug

  /// Enables debugging mode.
  enableDebugMode() {
    super.enableDebugMode()

    this.becomeVisible()
  }

  /// Creates a debug element.
  createDebugElement() {
    return new OnspaceVideoPlayerDebug({ player: this.player })
  }
}

window.customElements.define('onspace-video-controls', OnspaceVideoPlayerControls)
